import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Custom ISS icon
const issIcon = new L.Icon({
  iconUrl: "data:image/svg+xml;base64," + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#2563EB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="3" fill="#2563EB"/>
      <path d="M12 1v4M12 19v4M1 12h4M19 12h4"/>
      <path d="M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Florida marker icon
const floridaIcon = new L.Icon({
  iconUrl: "data:image/svg+xml;base64," + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="#DC2626" stroke="#DC2626" stroke-width="2">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3" fill="white"/>
    </svg>
  `),
  iconSize: [24, 24],
  iconAnchor: [12, 24],
  popupAnchor: [0, -24],
});

// Component to update map center when ISS moves
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom(), { animate: true });
    }
  }, [center, map]);
  return null;
};

export const ISSMap = ({ latitude, longitude, isOverFlorida, isNearby }) => {
  // Cape Canaveral coordinates
  const floridaPosition = [28.4, -80.6];
  const issPosition = [latitude || 0, longitude || 0];

  return (
    <div className="relative rounded-xl overflow-hidden" data-testid="iss-map">
      {/* Map Legend */}
      <div className="absolute top-3 right-3 z-[1000] bg-black/80 backdrop-blur-sm rounded-lg p-2 text-xs">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-cosmic-blue" />
          <span className="text-white">ISS Position</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-ignition-red" />
          <span className="text-white">Cape Canaveral</span>
        </div>
      </div>

      <MapContainer
        center={issPosition}
        zoom={3}
        style={{ height: "300px", width: "100%" }}
        scrollWheelZoom={false}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        
        {/* ISS Marker */}
        <Marker position={issPosition} icon={issIcon}>
          <Popup className="iss-popup">
            <div className="text-center">
              <strong className="text-cosmic-blue">ISS</strong>
              <br />
              <span className="text-xs">
                {latitude?.toFixed(4)}°, {longitude?.toFixed(4)}°
              </span>
              {isOverFlorida && (
                <div className="text-emerald-500 font-bold mt-1">Over Florida!</div>
              )}
              {isNearby && !isOverFlorida && (
                <div className="text-amber-500 mt-1">Nearby Florida</div>
              )}
            </div>
          </Popup>
        </Marker>

        {/* Florida Marker */}
        <Marker position={floridaPosition} icon={floridaIcon}>
          <Popup>
            <div className="text-center">
              <strong className="text-ignition-red">Cape Canaveral</strong>
              <br />
              <span className="text-xs">Florida, USA</span>
            </div>
          </Popup>
        </Marker>

        <MapUpdater center={issPosition} />
      </MapContainer>
    </div>
  );
};
